import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  Playlist,
  PlaylistList,
  Screen,
  ScreenList,
  SignageCandidate,
  SignageCandidateList,
} from '@up2d8/contracts';
import { SIGNAGE_DEFAULT_DURATION, SIGNAGE_REFRESH_SECONDS } from '@up2d8/contracts';
import { problemDetail } from '@up2d8/api-client';
import { useToast } from '@up2d8/ui';
import {
  ActionsCell,
  AdminIcon,
  AdminPage,
  Badge,
  Btn,
  Card,
  CardFoot,
  CardHead,
  CardPad,
  CodeDisplay,
  Empty,
  Field,
  FilterSelect,
  Grid,
  IconBtn,
  Input,
  Modal,
  Note,
  PageHead,
  Select,
  Table,
  useAdminToast,
} from '../admin-kit';
import { LoadError, Loading } from '../werkvloer';
import { useSession } from '../../shared/session';

/**
 * Een scherm dat in drie verversingsrondes niets vroeg, staat offline. Eén gemiste ronde is een
 * hik in het wifi, geen storing.
 */
const OFFLINE_AFTER_MS = 3 * SIGNAGE_REFRESH_SECONDS * 1000;

/**
 * Het beheer van digitale schermen (Stap 41; herontworpen in BHR-6, frame W9).
 *
 * Links de **schermen** (met hun stand en, zolang ze niet gekoppeld zijn, de koppelcode), rechts de
 * gekozen **playlist** met de berichten erop. Wat een beheerder erop kan zetten, komt uit de
 * kandidaten van de server: alleen wat ook echt op een scherm mag.
 *
 * Bij elke dia die niet draait, staat waarom. Dat is de belangrijkste keuze op dit scherm: zonder
 * die regel ziet een beheerder een lijst die klopt, terwijl het scherm in de kantine iets anders
 * toont — en dan is "het werkt niet" een vraag die niemand kan beantwoorden.
 */
export function SignageAdminPage() {
  const { t } = useTranslation();
  const { api } = useSession();
  const adminToast = useAdminToast();
  const [screenModal, setScreenModal] = useState<Screen | 'new' | null>(null);
  const [playlistModal, setPlaylistModal] = useState(false);
  const [code, setCode] = useState<Screen | null>(null);
  const [chosen, setChosen] = useState('');

  const screens = useQuery({
    queryKey: ['signage', 'screens'],
    queryFn: async (): Promise<Screen[]> => {
      const { data, error } = await api.GET('/api/v1/signage/screens', {});
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return (data as ScreenList).items;
    },
  });

  const playlists = useQuery({
    queryKey: ['signage', 'playlists'],
    queryFn: async (): Promise<Playlist[]> => {
      const { data, error } = await api.GET('/api/v1/signage/playlists', {});
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return (data as PlaylistList).items;
    },
  });

  const allPlaylists = playlists.data ?? [];
  // Zonder keuze de eerste playlist: met één reeks is kiezen een klik voor niets.
  const playlist = allPlaylists.find((candidate) => candidate.id === chosen) ?? allPlaylists[0];

  return (
    <AdminPage>
      <PageHead
        title={t('signage.title')}
        sub={t('beheer.werkvloer.signage.sub')}
        actions={
          <>
            <Btn
              variant="sec"
              icon={<AdminIcon name="plus" />}
              onClick={() => setPlaylistModal(true)}
              testId="playlist-new"
            >
              {t('signage.addPlaylist')}
            </Btn>
            <Btn
              icon={<AdminIcon name="plus" />}
              onClick={() => setScreenModal('new')}
              testId="screen-new"
            >
              {t('signage.addScreen')}
            </Btn>
          </>
        }
      />

      <Card>
        <CardPad>
          <Note tone="info">{t('beheer.werkvloer.signage.rule')}</Note>
        </CardPad>
      </Card>

      <Grid variant="two">
        <ScreensCard
          screens={screens}
          onEdit={(screen) => setScreenModal(screen)}
          onDone={adminToast.show}
        />

        <Card testId="playlist-card">
          <CardHead
            title={
              playlist
                ? t('beheer.werkvloer.signage.playlistTitle', { name: playlist.name })
                : t('signage.playlists')
            }
            count={playlist ? playlist.items.length : undefined}
            actions={
              allPlaylists.length > 1 && playlist ? (
                <FilterSelect
                  label={t('signage.playlist')}
                  value={playlist.id}
                  neutral=""
                  onChange={setChosen}
                  options={allPlaylists.map((candidate) => ({
                    value: candidate.id,
                    label: candidate.name,
                  }))}
                  testId="playlist-filter"
                />
              ) : null
            }
          />
          {playlists.isPending ? (
            <Loading />
          ) : playlists.isError ? (
            <LoadError onRetry={() => void playlists.refetch()} error={playlists.error} />
          ) : !playlist ? (
            <Empty
              icon={<AdminIcon name="monitor" size={24} />}
              title={t('signage.noPlaylistsTitle')}
              text={t('signage.noPlaylistsHint')}
              action={
                <Btn
                  variant="sec"
                  icon={<AdminIcon name="plus" />}
                  onClick={() => setPlaylistModal(true)}
                >
                  {t('signage.addPlaylist')}
                </Btn>
              }
            />
          ) : (
            <PlaylistItems playlist={playlist} onDone={adminToast.show} />
          )}
        </Card>
      </Grid>

      {screenModal ? (
        <ScreenModal
          screen={screenModal === 'new' ? null : screenModal}
          playlists={allPlaylists}
          onClose={() => setScreenModal(null)}
          onSaved={(saved, created) => {
            setScreenModal(null);
            adminToast.show(created ? t('signage.screenCreated') : t('common.saved'));
            if (created && saved?.pairingCode) setCode(saved);
          }}
        />
      ) : null}

      {playlistModal ? (
        <PlaylistModal
          onClose={() => setPlaylistModal(false)}
          onSaved={(created) => {
            setPlaylistModal(false);
            if (created) setChosen(created.id);
            adminToast.show(t('signage.playlistCreated'));
          }}
        />
      ) : null}

      {code ? <PairingModal screen={code} onClose={() => setCode(null)} /> : null}
      {adminToast.element}
    </AdminPage>
  );
}

function ScreensCard({
  screens,
  onEdit,
  onDone,
}: {
  screens: {
    data?: Screen[];
    isPending: boolean;
    isError: boolean;
    error?: unknown;
    refetch: () => unknown;
  };
  onEdit: (screen: Screen) => void;
  onDone: (message: string) => void;
}) {
  const { t } = useTranslation();
  const { api } = useSession();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [removing, setRemoving] = useState<Screen | null>(null);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['signage'] });

  const repair = useMutation({
    mutationFn: async (screenId: string) => {
      const { error } = await api.POST('/api/v1/signage/screens/{screenId}/pairing', {
        params: { path: { screenId } },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      void invalidate();
      onDone(t('signage.pairingReset'));
    },
    onError: (error) =>
      toast.show({ tone: 'danger', title: problemDetail(error) ?? t('errors.generic') }),
  });

  const remove = useMutation({
    mutationFn: async (screenId: string) => {
      const { error } = await api.DELETE('/api/v1/signage/screens/{screenId}', {
        params: { path: { screenId } },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      setRemoving(null);
      void invalidate();
    },
    onError: (error) =>
      toast.show({ tone: 'danger', title: problemDetail(error) ?? t('errors.generic') }),
  });

  const items = screens.data ?? [];

  return (
    <Card testId="signage-screens">
      <CardHead title={t('signage.screens')} count={screens.data ? items.length : undefined} />
      {screens.isPending ? (
        <Loading />
      ) : screens.isError ? (
        <LoadError onRetry={() => void screens.refetch()} error={screens.error} />
      ) : items.length === 0 ? (
        <Empty
          icon={<AdminIcon name="monitor" size={24} />}
          title={t('signage.noScreensTitle')}
          text={t('signage.noScreensHint')}
        />
      ) : (
        <Table label={t('signage.screens')}>
          <thead>
            <tr>
              <th>{t('beheer.werkvloer.signage.screen')}</th>
              <th>{t('signage.playlist')}</th>
              <th>{t('beheer.werkvloer.signage.status')}</th>
              <th className="act">
                <span className="a-sr">{t('common.actions')}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {items.map((screen) => (
              <tr key={screen.id} data-testid="screen-row">
                <td>
                  <div className="a-name">{screen.name}</div>
                  <div className="a-meta">{screen.location || t('signage.noLocation')}</div>
                </td>
                <td>{screen.playlistName ?? t('signage.noPlaylist')}</td>
                <td>
                  <ScreenStatus screen={screen} />
                </td>
                <ActionsCell>
                  <IconBtn
                    label={t('common.edit')}
                    icon={<AdminIcon name="pencil" />}
                    onClick={() => onEdit(screen)}
                    testId="screen-edit"
                  />
                  <Btn
                    variant="ghost"
                    size="sm"
                    disabled={repair.isPending}
                    onClick={() => repair.mutate(screen.id)}
                    testId="screen-repair"
                  >
                    {t('signage.newCode')}
                  </Btn>
                  <IconBtn
                    label={t('common.delete')}
                    icon={<AdminIcon name="trash" />}
                    onClick={() => setRemoving(screen)}
                    testId="screen-remove"
                  />
                </ActionsCell>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      <CardFoot>
        <span>
          {t('beheer.werkvloer.signage.footBefore')} <span className="a-mono">/signage</span>{' '}
          {t('beheer.werkvloer.signage.footAfter')}
        </span>
      </CardFoot>

      <Modal
        open={removing !== null}
        onClose={() => setRemoving(null)}
        title={t('beheer.werkvloer.signage.removeTitle', { name: removing?.name ?? '' })}
        footer={
          <>
            <Btn variant="sec" onClick={() => setRemoving(null)}>
              {t('common.cancel')}
            </Btn>
            <Btn
              variant="danger"
              disabled={remove.isPending}
              onClick={() => removing && remove.mutate(removing.id)}
              testId="screen-remove-confirm"
            >
              {t('common.delete')}
            </Btn>
          </>
        }
      >
        <Note tone="danger">{t('beheer.werkvloer.signage.removeText')}</Note>
      </Modal>
    </Card>
  );
}

/**
 * Online, offline sinds … of wacht op koppeling. Zolang een scherm niet gekoppeld is, staat de
 * koppelcode erbij: een beheerder leest hem voor aan wie bij het scherm staat. Daarna verdwijnt
 * hij — ook uit de database.
 */
function ScreenStatus({ screen }: { screen: Screen }) {
  const { t } = useTranslation();
  if (screen.pairingCode) {
    return (
      <>
        <Badge>{t('beheer.werkvloer.signage.waiting')}</Badge>
        <div className="a-meta" data-testid="screen-pairing-code">
          {t('signage.pairingCode', { code: screen.pairingCode })}
        </div>
      </>
    );
  }
  if (!screen.lastSeenAt) return <Badge>{t('signage.neverSeen')}</Badge>;
  const since = Date.now() - new Date(screen.lastSeenAt).getTime();
  if (since < OFFLINE_AFTER_MS)
    return <Badge tone="ok">{t('beheer.werkvloer.signage.online')}</Badge>;
  return (
    <Badge tone="attention">
      {t('beheer.werkvloer.signage.offlineSince', { since: ago(since, t) })}
    </Badge>
  );
}

/** "12 min", "2 u" of "3 d": kort genoeg voor een badge. */
function ago(ms: number, t: (key: string, options?: Record<string, unknown>) => string): string {
  const minutes = Math.round(ms / 60_000);
  if (minutes < 60) return t('beheer.werkvloer.signage.minutes', { count: minutes });
  const hours = Math.round(minutes / 60);
  if (hours < 48) return t('beheer.werkvloer.signage.hours', { count: hours });
  return t('beheer.werkvloer.signage.days', { count: Math.round(hours / 24) });
}

function PlaylistItems({
  playlist,
  onDone,
}: {
  playlist: Playlist;
  onDone: (message: string) => void;
}) {
  const { t } = useTranslation();
  const { api } = useSession();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);

  const removeItem = useMutation({
    mutationFn: async (itemId: string) => {
      const { error } = await api.DELETE('/api/v1/signage/playlists/{playlistId}/items/{itemId}', {
        params: { path: { playlistId: playlist.id, itemId } },
      });
      if (error) throw error;
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['signage'] }),
    onError: (error) =>
      toast.show({ tone: 'danger', title: problemDetail(error) ?? t('errors.generic') }),
  });

  return (
    <CardPad>
      <div className="a-meta">{t('signage.screenCount', { count: playlist.screenCount })}</div>
      {playlist.items.length === 0 ? (
        <p className="a-muted">{t('signage.emptyPlaylist')}</p>
      ) : (
        playlist.items.map((item) => (
          <div
            key={item.id}
            className="a-item"
            style={{ padding: '10px 0' }}
            data-testid="playlist-item"
          >
            <div className="a-item-info">
              <div className="a-name">{item.label}</div>
              <div className="a-meta">
                {t('signage.seconds', { count: item.durationSeconds })}
                {/* Waarom deze dia nu niet draait: dezelfde beoordeling als het scherm uitvoert. */}
                {!item.visible && item.hiddenReason ? (
                  <>
                    {' · '}
                    <span className="wv-dev">{item.hiddenReason}</span>
                  </>
                ) : null}
              </div>
            </div>
            <IconBtn
              label={t('beheer.werkvloer.signage.removeItem')}
              icon={<AdminIcon name="x" />}
              disabled={removeItem.isPending}
              onClick={() => removeItem.mutate(item.id)}
              testId="playlist-item-remove"
            />
          </div>
        ))
      )}
      <div>
        <Btn
          variant="sec"
          size="sm"
          icon={<AdminIcon name="plus" />}
          onClick={() => setAdding(true)}
          testId="playlist-add-open"
        >
          {t('signage.addPost')}
        </Btn>
      </div>
      {adding ? (
        <AddItemModal
          playlist={playlist}
          onClose={() => setAdding(false)}
          onSaved={() => {
            setAdding(false);
            onDone(t('signage.itemAdded'));
          }}
        />
      ) : null}
    </CardPad>
  );
}

function AddItemModal({
  playlist,
  onClose,
  onSaved,
}: {
  playlist: Playlist;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const { api } = useSession();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [postId, setPostId] = useState('');
  const [duration, setDuration] = useState(String(SIGNAGE_DEFAULT_DURATION));

  const candidates = useQuery({
    queryKey: ['signage', 'candidates'],
    queryFn: async (): Promise<SignageCandidate[]> => {
      const { data } = await api.GET('/api/v1/signage/candidates', {});
      return (data as SignageCandidateList | undefined)?.items ?? [];
    },
  });

  const addItem = useMutation({
    mutationFn: async () => {
      const { error } = await api.POST('/api/v1/signage/playlists/{playlistId}/items', {
        params: { path: { playlistId: playlist.id } },
        body: {
          type: 'post',
          refId: postId,
          durationSeconds: Number(duration) || SIGNAGE_DEFAULT_DURATION,
          validFrom: null,
          validUntil: null,
        },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['signage'] });
      onSaved();
    },
    onError: (error) =>
      toast.show({ tone: 'danger', title: problemDetail(error) ?? t('errors.generic') }),
  });

  return (
    <Modal
      open
      onClose={onClose}
      title={t('signage.addPost')}
      sub={playlist.name}
      footer={
        <>
          <Btn variant="sec" onClick={onClose}>
            {t('common.cancel')}
          </Btn>
          <Btn
            disabled={postId === '' || addItem.isPending}
            onClick={() => addItem.mutate()}
            testId="playlist-add-item"
          >
            {t('signage.addItem')}
          </Btn>
        </>
      }
    >
      <Field label={t('beheer.werkvloer.signage.post')} hint={t('signage.addPostHint')}>
        {(props) => (
          <Select
            {...props}
            value={postId}
            onChange={(event) => setPostId(event.target.value)}
            data-testid="playlist-post"
          >
            <option value="">{t('signage.choosePost')}</option>
            {(candidates.data ?? []).map((candidate) => (
              <option key={candidate.postId} value={candidate.postId}>
                {candidate.title}
                {candidate.onScreens ? ' ✓' : ''}
              </option>
            ))}
          </Select>
        )}
      </Field>
      <Field label={t('signage.duration')}>
        {(props) => (
          <Input
            {...props}
            inputMode="numeric"
            value={duration}
            onChange={(event) => setDuration(event.target.value)}
            data-testid="playlist-duration"
          />
        )}
      </Field>
    </Modal>
  );
}

/** Scherm toevoegen of wijzigen: naam, waar het hangt en de playlist. */
function ScreenModal({
  screen,
  playlists,
  onClose,
  onSaved,
}: {
  screen: Screen | null;
  playlists: Playlist[];
  onClose: () => void;
  onSaved: (screen: Screen | null, created: boolean) => void;
}) {
  const { t } = useTranslation();
  const { api } = useSession();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [name, setName] = useState(screen?.name ?? '');
  const [location, setLocation] = useState(screen?.location ?? '');
  const [playlistId, setPlaylistId] = useState(screen?.playlistId ?? '');

  const save = useMutation({
    mutationFn: async (): Promise<Screen | null> => {
      if (screen) {
        const { error } = await api.PATCH('/api/v1/signage/screens/{screenId}', {
          params: { path: { screenId: screen.id } },
          body: { name, location, playlistId: playlistId || null },
        });
        if (error) throw error;
        return null;
      }
      const { data, error } = await api.POST('/api/v1/signage/screens', {
        // Eén vestiging is de standaard; een scherm dat dit veld weglaat zou bij de eerste
        // klant met twee sites stil de verkeerde kiezen (Stap 29).
        body: { name, location, playlistId: playlistId || null, siteId: null },
      });
      if (error) throw error;
      return (data as Screen | undefined) ?? null;
    },
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: ['signage'] });
      onSaved(saved, screen === null);
    },
    onError: (error) =>
      toast.show({ tone: 'danger', title: problemDetail(error) ?? t('errors.generic') }),
  });

  return (
    <Modal
      open
      onClose={onClose}
      title={screen ? t('beheer.werkvloer.signage.editScreen') : t('signage.addScreen')}
      footer={
        <>
          <Btn variant="sec" onClick={onClose}>
            {t('common.cancel')}
          </Btn>
          <Btn
            disabled={name.trim() === '' || save.isPending}
            onClick={() => save.mutate()}
            testId="screen-create"
          >
            {screen ? t('common.save') : t('signage.addScreen')}
          </Btn>
        </>
      }
    >
      <Field label={t('signage.screenName')}>
        {(props) => (
          <Input
            {...props}
            value={name}
            maxLength={80}
            onChange={(event) => setName(event.target.value)}
            data-testid="screen-name"
          />
        )}
      </Field>
      <Field label={t('signage.screenLocation')} optional>
        {(props) => (
          <Input
            {...props}
            value={location}
            maxLength={120}
            onChange={(event) => setLocation(event.target.value)}
            data-testid="screen-location"
          />
        )}
      </Field>
      <Field label={t('signage.playlist')}>
        {(props) => (
          <Select
            {...props}
            value={playlistId}
            onChange={(event) => setPlaylistId(event.target.value)}
            data-testid="screen-new-playlist"
          >
            <option value="">{t('signage.noPlaylist')}</option>
            {playlists.map((playlist) => (
              <option key={playlist.id} value={playlist.id}>
                {playlist.name}
              </option>
            ))}
          </Select>
        )}
      </Field>
    </Modal>
  );
}

function PlaylistModal({
  onClose,
  onSaved,
}: {
  onClose: () => void;
  onSaved: (created: Playlist | null) => void;
}) {
  const { t } = useTranslation();
  const { api } = useSession();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');

  const create = useMutation({
    mutationFn: async (): Promise<Playlist | null> => {
      const { data, error } = await api.POST('/api/v1/signage/playlists', { body: { name } });
      if (error) throw error;
      return (data as Playlist | undefined) ?? null;
    },
    onSuccess: (created) => {
      void queryClient.invalidateQueries({ queryKey: ['signage'] });
      onSaved(created);
    },
    onError: (error) =>
      toast.show({ tone: 'danger', title: problemDetail(error) ?? t('errors.generic') }),
  });

  return (
    <Modal
      open
      onClose={onClose}
      title={t('signage.addPlaylist')}
      sub={t('signage.noPlaylistsHint')}
      footer={
        <>
          <Btn variant="sec" onClick={onClose}>
            {t('common.cancel')}
          </Btn>
          <Btn
            disabled={name.trim() === '' || create.isPending}
            onClick={() => create.mutate()}
            testId="playlist-create"
          >
            {t('signage.addPlaylist')}
          </Btn>
        </>
      }
    >
      <Field label={t('signage.playlistName')}>
        {(props) => (
          <Input
            {...props}
            value={name}
            maxLength={80}
            onChange={(event) => setName(event.target.value)}
            data-testid="playlist-name"
          />
        )}
      </Field>
    </Modal>
  );
}

/** De koppelcode van een nieuw scherm, zoals bij een kiosk (W8). */
function PairingModal({ screen, onClose }: { screen: Screen; onClose: () => void }) {
  const { t } = useTranslation();
  return (
    <Modal
      open
      onClose={onClose}
      title={screen.location ? `${screen.name} · ${screen.location}` : screen.name}
      testId="screen-code-dialog"
      footer={<Btn onClick={onClose}>{t('beheer.werkvloer.done')}</Btn>}
    >
      <p>{t('signage.howTo')}</p>
      <CodeDisplay
        code={screen.pairingCode ?? ''}
        label={t('beheer.werkvloer.signage.codeLabel')}
        warning={t('beheer.werkvloer.signage.codeStays')}
      />
    </Modal>
  );
}
